import { useEffect, useState } from 'react';
import ZestyApi from '@apis/zesty.apis';
import { Promo } from '@apis/models/zesty.api.model';
import { Address } from '@app/models';
import { getBrand } from '@helpers/brand.utils';

interface UseZestyPromosResult {
  validPromo: Promo | null;
  showBanner: boolean;
  promoData: Promo[] | null;
  promoCode: string;
  isLoading: boolean;
}

export const useZestyPromos = (
  address?: Address,
  skipPageValidation?: boolean,
): UseZestyPromosResult => {
  const [promoData, setPromoData] = useState<Promo[]>(null);
  const [validPromo, setValidPromo] = useState<Promo>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [partnerPortalPages, setPartnerPortalPages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    Promise.all([ZestyApi.GetPartnerPortalPages(), ZestyApi.GetPromoBanner()])
      .then(([pages, promos]) => {
        if (!isMounted) return;
        setPartnerPortalPages(pages);
        setPromoData(promos);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (promoData && address?.state && partnerPortalPages) {
      const promo = checkAndShowPromo(address.state);
      if (promo) {
        setValidPromo(promo);
        setShowBanner(true);
      } else {
        setValidPromo(null);
        setShowBanner(false);
      }
    } else {
      setShowBanner(false);
      setValidPromo(null);
    }
  }, [address, promoData, partnerPortalPages]);

  const checkAndShowPromo = (state: string): Promo | undefined => {
    const date = new Date();
    const currentBrand = getBrand();

    return promoData.find((promo) => {
      const startDate = new Date(promo.startDate);
      const endDate = new Date(promo.endDate);

      const inDateRange = date > startDate && date < endDate;

      const pageActive =
        skipPageValidation ||
        promo.displayPages.filter((page) => partnerPortalPages.includes(page)).length > 0;

      const brandMatches = promo.brand?.toUpperCase() === currentBrand.toUpperCase();

      return (
        promo.states.includes(state) &&
        promo.status === 'on' &&
        pageActive &&
        inDateRange &&
        brandMatches
      );
    });
  };

  return {
    validPromo,
    showBanner,
    promoData,
    promoCode: validPromo?.promoCode || '',
    isLoading,
  };
};
